/**
 * Guest email matching (Sunday hardening, 16 Oct 2026; G2 in
 * .agents/tasks/sunday-2-checkin-catch-paths.md).
 *
 * A guest's catch is matched to their competition registration by
 * participantEmail, an exact DynamoDB key. Registrations made before this fix
 * stored the email exactly as typed, so a guest who typed a capital letter or
 * a trailing space at either step got "No competition registration found".
 *
 * - New guest registrations store the email trimmed and lower-cased
 *   (normaliseGuestEmail).
 * - Lookups try the email trimmed as typed first (matches older rows stored in
 *   mixed case), then its trimmed lower-case form (matches new rows). Stored
 *   values are never changed.
 */

export function normaliseGuestEmail(email: unknown): string {
  return typeof email === 'string' ? email.trim().toLowerCase() : '';
}

/** The participantEmail values to look up, in order, without duplicates or blanks. */
export function guestEmailCandidates(email: unknown): string[] {
  if (typeof email !== 'string') return [];
  const trimmed = email.trim();
  if (!trimmed) return [];
  const lower = trimmed.toLowerCase();
  return trimmed === lower ? [trimmed] : [trimmed, lower];
}

/**
 * Runs `lookup` for each candidate until one returns a registration for the
 * event. `lookup(email)` returns the registrations with that participantEmail.
 */
export async function findGuestRegistration<R extends { eventId?: string | null }>(
  email: unknown,
  eventId: string,
  lookup: (candidate: string) => Promise<R[]>,
): Promise<R | null> {
  for (const candidate of guestEmailCandidates(email)) {
    const match = (await lookup(candidate)).find((r) => r && r.eventId === eventId);
    if (match) return match;
  }
  return null;
}
